/**
 * PageMetrics.ts
 * 
 * Derives page dimensions in world pixels (96 DPI) from WASM PageSetup.
 * Uses FixedPoint conversion to ensure consistency with PageSetupConverter.
 * 
 * This is the single source of truth for page dimensions.
 */

import { FixedPoint, PageSetup } from '../core/types/Typesetter';

export type PageMetricsPx = {
  pageWidthPx: number;
  pageHeightPx: number;
  contentX: number;
  contentY: number;
  contentW: number;
  contentH: number;
};

/**
 * Extracts page metrics in world pixels from WASM PageSetup.
 * 
 * @param wasmPageSetup - PageSetup from WASM (26.6 fixed-point)
 * @returns Page dimensions in world pixels (96 DPI)
 */
export function getPageMetricsPx(wasmPageSetup: PageSetup): PageMetricsPx {
  const m = wasmPageSetup.margins_fp;
  const r = wasmPageSetup.content_rect_fp;
  
  // Convert fixed-point to world pixels
  const contentW = FixedPoint.toPx(r.w_fp);
  const contentH = FixedPoint.toPx(r.h_fp);
  const contentX = FixedPoint.toPx(r.x_fp);
  const contentY = FixedPoint.toPx(r.y_fp);
  
  // Page dimensions = content + margins
  const marginLeft = FixedPoint.toPx(m.left);
  const marginRight = FixedPoint.toPx(m.right);
  const marginTop = FixedPoint.toPx(m.top);
  const marginBottom = FixedPoint.toPx(m.bottom);
  
  const pageWidthPx = contentW + marginLeft + marginRight;
  const pageHeightPx = contentH + marginTop + marginBottom;
  
  return {
    pageWidthPx,
    pageHeightPx,
    contentX,
    contentY,
    contentW,
    contentH,
  };
}

